import { useI18n } from 'vue-i18n';
import { DatetimeFormatType } from './config/datetime-formats.config.ts';
import { toISODateOnlyString } from './utils/dates.utils.ts';
import type { LocaleType } from './config/locales.ts';
import type { DateLike } from './date.types.ts';
import { isToday, isTomorrow, isValid } from 'date-fns';
import {
  BASE_CURRENCY_DIVISION,
  NumberFormatType,
} from './config/number-formats.config.ts';
import { computed } from 'vue';
import { formatPhoneNumber } from './utils/phoneNumber.utils.ts';

type Tail<T extends unknown[]> = T extends [unknown, ...infer U] ? U : never;

// Extract all the arguments except the first
type ArgumentsExceptFirst<T> = T extends (...args: infer Args) => unknown
  ? Tail<Args>
  : never;

export const italianDateFormat = 'dd/MM/yyyy';
export const italianMonthYearFormat = 'MM/yyyy';

const localeDateStringParsers: Record<LocaleType, string> = {
  it: italianDateFormat,
};

function isValidDate(date: Date | null): date is Date {
  return isValid(date);
}

export const useLocale = (namespace?: string) => {
  const { t: oldT, d: oldD, n: oldN, ...rest } = useI18n();

  const tWithNamespace = ((
    key: string,
    ...args: ArgumentsExceptFirst<typeof oldT>
  ) => {
    let finalKey = key;
    let finalNamespace = namespace;

    // if the key contains a colon, we take the value before as a namespace and extract the key
    if (key?.includes(':')) {
      finalNamespace = key.split(':')[0];
      finalKey = key.split(':')[1];
    }
    const tKey = finalNamespace ? `${finalNamespace}.${finalKey}` : finalKey;
    return oldT(tKey, ...args);
  }) as typeof oldT;

  const currentLanguageDateFormat = computed(() => {
    return localeDateStringParsers[rest.locale.value as LocaleType];
  });

  // TODO: in future we will transform parameters to object to avoid passing too many arguments
  const augmentedD = (
    date: DateLike,
    formatType: DatetimeFormatType,
    disableTodayString?: boolean,
    disableTomorrowString?: boolean,
  ): string => {
    let parsedDate: Date | null = null;
    if (date instanceof Date) {
      parsedDate = date;
    }
    if (
      typeof date === 'number' ||
      (typeof date === 'string' &&
        formatType !== DatetimeFormatType.DATE_ONLY_ISO)
    ) {
      parsedDate = new Date(date);
    }

    if (!isValidDate(parsedDate)) {
      throw new Error(`Invalid date provided: ${date}`);
    }
    if (
      !disableTodayString &&
      formatType !== DatetimeFormatType.DATETIME &&
      isToday(parsedDate)
    ) {
      return tWithNamespace('common:words.today');
    }
    if (
      !disableTomorrowString &&
      formatType !== DatetimeFormatType.DATETIME &&
      isTomorrow(parsedDate)
    ) {
      return tWithNamespace('common:words.tomorrow');
    }
    if (formatType === DatetimeFormatType.DATE_ONLY_ISO) {
      return toISODateOnlyString(parsedDate);
    }
    return oldD(parsedDate, formatType);
  };

  const augmentedN = (
    number: number | null | undefined,
    formatType: NumberFormatType,
    showZeroAsGratis?: boolean,
    zeroLabel?: string,
  ): string => {
    if (number === undefined || number === null) return '';
    let finalNumber = number;
    if (formatType === NumberFormatType.CURRENCY) {
      finalNumber = number / BASE_CURRENCY_DIVISION;
      if (showZeroAsGratis && finalNumber === 0) {
        return zeroLabel || tWithNamespace('common:prices.gratis');
      }
    }
    return oldN(finalNumber, formatType);
  };

  const p = (phoneNumber?: string | null): string => {
    if (!phoneNumber) {
      console.warn('Invalid phone number', phoneNumber);
      return '';
    }
    return formatPhoneNumber(phoneNumber);
  };

  return {
    t: tWithNamespace,
    d: augmentedD,
    n: augmentedN,
    p,
    currentLanguageDateFormat,
    ...rest,
  };
};
